import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { searchCVE, fetchCVEStats } from "../../actions/cveInformation";
import Pagination from "../Pagination/Pagination";
import moment from "moment";

const CVEInformation = () => {
  const dispatch = useDispatch();
  const { data } = useParams();
  const [vendor, setVendor] = useState("");
  const [name, setName] = useState("");
  const [versionMajor, setVersionMajor] = useState("");
  const [versionMinor, setVersionMinor] = useState("");
  const [versionPatch, setVersionPatch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [hasSearched, setHasSearched] = useState(false);
  
  const { vendorName, productName } = data 
    ? Object.fromEntries(new URLSearchParams(data))
    : { vendorName: undefined, productName: undefined };

  const cveFunction = () => {
    setPage(1);
    setHasSearched(true);
    searchCVE(
      dispatch,
      {
        vendor: vendor,
        name: name,
        versionMajor: versionMajor,
        versionMinor: versionMinor,
        versionPatch: versionPatch,
      },
      1,
      limit
    );
  };

  const cveData = useSelector((state) => state?.cveInformation?.response?.data);
  const cveStats = useSelector((state) => state?.cveInformation?.stats?.data);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize on mount - load default CVEs or use URL params
  useEffect(() => {
    if (vendorName !== undefined && vendorName !== "") {
      setVendor(vendorName);
    }

    if (productName !== undefined && productName !== "") {
      setName(productName);
    }

    // Fetch CVE stats
    fetchCVEStats(dispatch);

    // If vendorName and productName are provided from URL, search with them
    if (vendorName !== undefined && productName !== undefined && vendorName !== "" && productName !== "") {
      setHasSearched(true);
      searchCVE(
        dispatch,
        {
          vendor: vendorName,
          name: productName,
          versionMajor: versionMajor,
          versionMinor: versionMinor,
          versionPatch: versionPatch,
        },
        page,
        limit
      );
    } else {
      // Load default CVEs when there's no search params
      searchCVE(
        dispatch,
        {},
        page,
        limit
      );
    }
    setIsInitialized(true);
  }, []); // Only run on mount

  // Handle page/limit changes after initialization
  useEffect(() => {
    if (!isInitialized) return;

    const hasSearchParams = (vendorName && productName) || hasSearched || vendor || name || versionMajor || versionMinor || versionPatch;
    
    if (hasSearchParams) {
      const searchParams = (vendorName && productName) 
        ? {
            vendor: vendorName,
            name: productName,
            versionMajor: versionMajor,
            versionMinor: versionMinor,
            versionPatch: versionPatch,
          }
        : {
            vendor: vendor,
            name: name,
            versionMajor: versionMajor,
            versionMinor: versionMinor,
            versionPatch: versionPatch,
          };
      
      searchCVE(dispatch, searchParams, page, limit);
    } else {
      // Load default CVEs when paginating without search
      searchCVE(dispatch, {}, page, limit);
    }
  }, [page, limit, isInitialized]);

  return (
    <div className="container-fluid">
      <div className="block-header">
        <div className="row clearfix">
          <div className="col-md-6 col-sm-12">
            <h2 >CVE Information</h2>
            <nav aria-label="breadcrumb" className="mt-1">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/" className="welcome-textSmall">Home</Link>
                </li>
                <li
                  className="breadcrumb-item active"
                  aria-current="page"
                  style={{ color: "white" }}
                >
                  CVE Information
                </li>
              </ol>
            </nav>
          </div>
          {/* <div className="col-md-6 col-sm-12 text-right hidden-xs">
              <Link to="/" className="btn btn-sm btn-primary mr-1" title="">
                Create Campaign
              </Link>
              <Link
                to="https://themeforest.net/item/BrandSek-bootstrap-4x-admin-dashboard-clean-modern-ui-kit/23091507"
                className="btn btn-sm btn-success"
                title="Themeforest"
              >
                <i className="icon-basket"></i> Buy Now
              </Link>
            </div> */}
        </div>
      </div>
      
      {/* Exploited Vulnerabilities Statistics */}
      {cveStats && (
        <div className="row clearfix mb-4">
          <div className="col-12">
            <div className="card" style={{ boxShadow: "0 2px 10px rgba(0,0,0,0.1)", border: "none", borderRadius: "8px" }}>
              <div className="body" style={{ padding: "25px" }}>
                <h5 className="mb-4" style={{ fontWeight: "600", color: "#2c3e50", fontSize: "1.25rem" }}>
                  <i className="fa fa-shield-alt mr-2" style={{ color: "#dc3545" }}></i>
                  Exploited Vulnerabilities
                </h5>
                
                <div className="row">
                  <div className="col-lg-4 col-md-6 mb-3">
                    <div
                      className="card border-0"
                      style={{
                        background: "#fff",
                        borderRadius: "12px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        transition: "all 0.3s ease",
                        borderLeft: "4px solid #ffc107",
                        height: "100%"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-3px)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                      }}
                    >
                      <div className="body" style={{ padding: "20px" }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <div style={{ flex: 1 }}>
                            <p className="mb-1" style={{ fontSize: "0.875rem", color: "#6c757d", fontWeight: "500" }}>
                              Since Yesterday
                            </p>
                            <h3 className="mb-0 mt-2" style={{ fontWeight: "700", fontSize: "2rem", color: "#212529" }}>
                              {cveStats?.exploitedVulnerabilitiesStats?.sinceYesterday || 0}
                            </h3>
                            <p className="mb-0 mt-1" style={{ fontSize: "0.75rem", color: "#6c757d" }}>
                              Exploited
                            </p>
                          </div>
                          <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                              width: "56px",
                              height: "56px",
                              borderRadius: "12px",
                              background: "linear-gradient(135deg, #ffc107 0%, #ffb300 100%)",
                              color: "#fff",
                              fontSize: "24px"
                            }}
                          >
                            <i className="fa fa-exclamation-triangle"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6 mb-3">
                    <div
                      className="card border-0"
                      style={{
                        background: "#fff",
                        borderRadius: "12px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        transition: "all 0.3s ease",
                        borderLeft: "4px solid #dc3545",
                        height: "100%"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-3px)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                      }}
                    >
                      <div className="body" style={{ padding: "20px" }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <div style={{ flex: 1 }}>
                            <p className="mb-1" style={{ fontSize: "0.875rem", color: "#6c757d", fontWeight: "500" }}>
                              Last 7 Days
                            </p>
                            <h3 className="mb-0 mt-2" style={{ fontWeight: "700", fontSize: "2rem", color: "#212529" }}>
                              {cveStats?.exploitedVulnerabilitiesStats?.last7Days || 0}
                            </h3>
                            <p className="mb-0 mt-1" style={{ fontSize: "0.75rem", color: "#6c757d" }}>
                              Exploited
                            </p>
                          </div>
                          <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                              width: "56px",
                              height: "56px",
                              borderRadius: "12px",
                              background: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
                              color: "#fff",
                              fontSize: "24px"
                            }}
                          >
                            <i className="fa fa-shield-alt"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6 mb-3">
                    <div
                      className="card border-0"
                      style={{
                        background: "#fff",
                        borderRadius: "12px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        transition: "all 0.3s ease",
                        borderLeft: "4px solid #343a40",
                        height: "100%"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-3px)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                      }}
                    >
                      <div className="body" style={{ padding: "20px" }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <div style={{ flex: 1 }}>
                            <p className="mb-1" style={{ fontSize: "0.875rem", color: "#6c757d", fontWeight: "500" }}>
                              Last 30 Days
                            </p>
                            <h3 className="mb-0 mt-2" style={{ fontWeight: "700", fontSize: "2rem", color: "#212529" }}>
                              {cveStats?.exploitedVulnerabilitiesStats?.last30Days || 0}
                            </h3>
                            <p className="mb-0 mt-1" style={{ fontSize: "0.75rem", color: "#6c757d" }}>
                              Exploited
                            </p>
                          </div>
                          <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                              width: "56px",
                              height: "56px",
                              borderRadius: "12px",
                              background: "linear-gradient(135deg, #343a40 0%, #23272b 100%)",
                              color: "#fff",
                              fontSize: "24px"
                            }}
                          >
                            <i className="fa fa-bug"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="row">
        <div className="col-lg-2 col-md-6">
          <div className="input-group">
            <input
              placeholder="Vendor Name"
              value={vendor}
              name="name"
              onChange={(e) => {
                setVendor(e.target.value);
              }}
              className="form-control"
            ></input>
          </div>
        </div>
        <div className="col-lg-2 col-md-6">
          <div className="input-group">
            <input
              placeholder="Product Name"
              value={name}
              name="name"
              onChange={(e) => {
                setName(e.target.value);
              }}
              className="form-control"
            ></input>
          </div>
        </div>
        <div className="col-lg-2 col-md-6">
          <div className="input-group">
            <input
              placeholder="Version Major"
              value={versionMajor}
              name="version-major"
              type="text"
              step={1}
              onChange={(e) => {
                const value = e.target.value;
                setVersionMajor(value === "" ? "" : (isNaN(Number(value)) ? "" : Math.round(Number(value))));
              }}
              className="form-control"
            ></input>
          </div>
        </div>
        <div className="col-lg-2 col-md-6">
          <div className="input-group">
            <input
              value={versionMinor}
              name="version-minor"
              placeholder="version-minor"
              type="text"
              step={1}
              onChange={(e) => {
                const value = e.target.value;
                setVersionMinor(value === "" ? "" : (isNaN(Number(value)) ? "" : Math.round(Number(value))));
              }}
              className="form-control"
            ></input>
          </div>
        </div>
        <div className="col-lg-2 col-md-6">
          <div className="input-group">
            <input
              placeholder="version-patch"
              value={versionPatch}
              name="version-patch"
              type="text"
              step={1}
              onChange={(e) => {
                const value = e.target.value;
                setVersionPatch(value === "" ? "" : (isNaN(Number(value)) ? "" : Math.round(Number(value))));
              }}
              className="form-control"
            ></input>
          </div>
        </div>
        <div className="col-lg-2 col-md-6">
          <button
            onClick={cveFunction}
            type="button"
            style={{ fontWeight: "bold" }}
            className="btn btn-sm btn-success"
          >
            Search
          </button>
        </div>
      </div>
      {cveData && cveData.cves && cveData.cves.length > 0 ? (
        <div className="col-md-12">
          <div className="table-responsive">
            <table className="table table-custom spacing5 mb-0 mt-3">
              <thead>
                <tr>
                  <th>CVEs</th>
                  <th>Description</th>
                  <th>CVSS Score</th>
                  <th>Severity</th>
                  <th>EPSS</th>
                  <th>CISA Ransomware</th>
                  <th>Published Date</th>
                  <th>Last Modified Date</th>
                </tr>
              </thead>
              <tbody>
                {cveData?.cves?.map((cve, i) => {
                  const getSeverityBadgeClass = (severity) => {
                    if (!severity) return "badge-secondary";
                    const severityUpper = severity.toUpperCase();
                    if (severityUpper === "CRITICAL") return "badge-danger";
                    if (severityUpper === "HIGH") return "badge-warning";
                    if (severityUpper === "MEDIUM") return "badge-info";
                    if (severityUpper === "LOW") return "badge-success";
                    return "badge-secondary";
                  };

                  return (
                    <tr key={i}>
                      <td>
                        <Link
                          to={`/cve/${cve?.cveId}`}
                          className="p-2 rounded-md bg-slate-200"
                        >
                          {cve?.cveId}
                        </Link>
                      </td>
                      <td>
                        <p style={{ maxWidth: "400px", textWrap: "balance" }}>
                          {cve?.cveDescription}
                        </p>
                      </td>
                      <td>
                        {cve?.cvssScore !== null && cve?.cvssScore !== undefined
                          ? cve.cvssScore.toFixed(1)
                          : "N/A"}
                      </td>
                      <td>
                        {cve?.cvssBaseSeverity ? (
                          <span className={`badge ${getSeverityBadgeClass(cve.cvssBaseSeverity)}`}>
                            {cve.cvssBaseSeverity}
                          </span>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        {cve?.epss !== null && cve?.epss !== undefined
                          ? (cve.epss * 100).toFixed(2) + "%"
                          : "N/A"}
                      </td>
                      <td>
                        {cve?.cisa_knownRansomwareCampaignUse && cve.cisa_knownRansomwareCampaignUse !== ""
                          ? (
                              <span className="badge badge-danger">
                                {cve.cisa_knownRansomwareCampaignUse}
                              </span>
                            )
                          : "N/A"}
                      </td>
                      <td>
                        {cve?.publishedDate
                          ? moment(cve?.publishedDate).format(
                              "DD-MM-YYYY"
                            )
                          : ""}
                      </td>
                      <td>
                        {cve?.lastModifiedDate
                          ? moment(cve?.lastModifiedDate).format(
                              "DD-MM-YYYY"
                            )
                          : ""}
                      </td>
                    </tr>
                  );
                })}
                {/* <tr>
               <td>
                 <img
                   src="../assets/images/xs/avatar1.jpg"
                   alt="Avatar"
                   className="w30 rounded-circle mr-2"
                 />{" "}
                 <span>Isidore Dilao</span>
               </td>
               <td>
                 <h6 className="mb-0">account@something.com</h6>
               </td>
               <td>
                 <h6 className="mb-0">+1 (561) 289-0454</h6>
               </td>
               <td>
                 <h6 className="mb-0">Threat Analyst</h6>
               </td>
               <td>
                 <span className="badge badge-success">Completed</span>
               </td>
               <td>
                 <h6 className="mb-0">22/05/2022 14:14</h6>
               </td>
               <td>
                 <button className="btn btn-primary btn-sm mr-1">
                   Edit
                 </button>
                 <button className="btn btn-danger btn-sm">
                   Delete
                 </button>
               </td>
             </tr> */}
              </tbody>
            </table>
          </div>
          <div className="dataTables_paginate">
            {cveData?.totalPages > 0 && (
              <Pagination
                className="pagination-bar"
                currentPage={page}
                totalCount={cveData?.totalCves}
                pageSize={limit}
                onPageChange={(page) => {
                  setPage(page);
                  const searchParams = (vendorName && productName) 
                    ? {
                        vendor: vendorName,
                        name: productName,
                        versionMajor: versionMajor,
                        versionMinor: versionMinor,
                        versionPatch: versionPatch,
                      }
                    : {
                        vendor: vendor,
                        name: name,
                        versionMajor: versionMajor,
                        versionMinor: versionMinor,
                        versionPatch: versionPatch,
                      };
                  
                  if (hasSearched || (vendorName && productName) || vendor || name || versionMajor || versionMinor || versionPatch) {
                    searchCVE(dispatch, searchParams, page, limit);
                  } else {
                    searchCVE(dispatch, {}, page, limit);
                  }
                }}
              />
            )}
          </div>
        </div>
      ) : (
        <p className="text-center mt-5">No cves</p>
      )}
    </div>
  );
};
export default CVEInformation;
